import { useState } from "react";

import { cancelCreditsRecharge, createCreditsRecharge } from "API/calls";
import { useUserDataContext } from "context/UserDataContext";
import { useAPICreditsPaymentTypes, useAPICreditsRecharge, useAPIUserInvoiceData } from "hooks/useAPI";
import useConfirm from "hooks/useConfirm";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RechargeState, RechargeType } from "types/enums";
import { ICreditRecharge } from "types/types";

const solveType = (
  isNewOrActive: boolean,
  type: RechargeType,
  prevType?: RechargeType,
  data?: ICreditRecharge
): RechargeType => {
  if (isNewOrActive) {
    if (prevType && data?.type === prevType) return prevType;

    return type;
  }

  return type;
};

const getStatus = (state?: RechargeState) => {
  if (state === RechargeState.CANCELED) {
    return RechargeState.INACTIVE;
  }

  return state;
};

export const useRechargeCard = (defaultPrice: number,type: RechargeType, prevType?: RechargeType) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm } = useConfirm();
  const [isOpenBackdropLoading, setIsOpenBackdropLoading] = useState<boolean>(false);
  const [invoiceSetupOpen, setInvoiceSetupOpen] = useState(false);
  
  const { refreshCreditsRecharge, subscriptionType } = useUserDataContext();
  const { data, mutate, response } = useAPICreditsRecharge();
  const { data: userInvoiceData, mutate: userInvoiceRefetch } = useAPIUserInvoiceData();
  const { data: creditsPaymentTypesData, mutate: creditsPaymentTypesRefetch } = useAPICreditsPaymentTypes();

  const isNewOrActive = data?.state === RechargeState.ACTIVE || data?.state === RechargeState.NEW;
  const rechargeType = solveType(isNewOrActive, type, prevType, data);
  const paymentType = creditsPaymentTypesData?.creditsPaymentTypes?.[rechargeType];
  const isActive = isNewOrActive ? data?.type === rechargeType : false;
  const price = paymentType?.czkAmountWithTax ?? defaultPrice;
  const priceWithoutTax = paymentType?.czkAmountWithoutTax;
  const creditsAmount = paymentType?.creditsAmount;
  const status = getStatus(data?.state);

  const handleCancel = async (callback?: () => void, withActive?: boolean) => {
    try {
      await confirm({
        title: t(`recharge.${withActive ? "cancelActive" : "confirmCancel"}.title`),
        description: t(`recharge.${withActive ? "cancelActive" : "confirmCancel"}.description`, { amount: creditsAmount }),
        dialogProps: { maxWidth: "xs" },
      });

      setIsOpenBackdropLoading(true);

      try {
        await cancelCreditsRecharge();

        if(callback) callback();
        else {
          mutate();
          enqueueSnackbar(t("recharge.confirmCancel.cancelSuccess"), { variant: "success" });
          setIsOpenBackdropLoading(false);
          refreshCreditsRecharge?.();
        }
      } catch (error: any) {
        if (error?.response?.status === 404) {
          enqueueSnackbar(t("recharge.confirmCancel.error404"), { variant: "error" });
        } else {
          enqueueSnackbar(t("recharge.confirmCancel.cancelError"), { variant: "error" });
        }
        setIsOpenBackdropLoading(false);
      }
    } catch (err: any) {
      /* silent */
    }
  };

  const handleCreate = async (type: RechargeType) => {
    setIsOpenBackdropLoading(true);

    try {
      const { data } = await createCreditsRecharge(type);
      window.location.replace(data?.redirectUrl);
      enqueueSnackbar(t("recharge.createSuccess"), { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(t("recharge.createError"), { variant: "error" });
    }
    setIsOpenBackdropLoading(false);
  };

  const handleActivate = async () => {
    if (data?.state === RechargeState.NEW || data?.state === RechargeState.ACTIVE) {
      handleCancel(() => handleCreate(type), true);
    } else {
      handleCreate(type);
    }
  };

   const handleRefreshInvoiceSetup = () => {
    userInvoiceRefetch();
    creditsPaymentTypesRefetch();
  };

  return {
    isOpenBackdropLoading,
    setIsOpenBackdropLoading,
    isActive,
    status,
    price,
    priceWithoutTax,
    creditsAmount,
    userInvoiceData,
    rechargeType,
    handleCancel,
    data,
    responseStatus: response?.status,
    creditsPaymentTypesData,
    subscriptionType,
    handleActivate,
    invoiceSetupOpen,
    setInvoiceSetupOpen,
    handleRefreshInvoiceSetup,
  };
};
